import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Loader2, Check, Sparkles, Rocket, Target, Flag } from "lucide-react";
import { Logo } from "@/components/Common";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";
import { toast } from "sonner";

// Essential question drives the recommendation; the rest are skippable.
const QUESTIONS = [
  {
    key: "stage", essential: true,
    q: "Where are you right now?", help: "This tells us where to point you first.",
    options: ["I only have an idea", "I am refining an idea", "I already have an offer", "I am preparing to launch"],
  },
  {
    key: "business_type",
    q: "What kind of business are you drawn to?", help: "You can change direction later.",
    options: ["A digital product", "A service", "Content / creator", "Not sure yet"],
  },
  {
    key: "launch_goal",
    q: "What's your primary goal?", help: "Your finish line for now.",
    options: ["Make my first sale", "Replace some income", "Build something of my own", "Learn by doing"],
  },
  {
    key: "weekly_commitment",
    q: "How much time can you give this each week?", help: "We'll size the steps to fit your life.",
    options: ["Under 5 hours", "5 to 10 hours", "10 to 20 hours", "20+ hours"],
  },
];

const OUTPUTS = [
  "Purpose Statement", "Opportunity Note", "Business Description", "Customer Profile",
  "Validation Plan", "Offer Document", "Launch Kit",
];

// Recommend a starting emphasis based on the user's current stage.
function recommendation(stage) {
  switch (stage) {
    case "I am refining an idea":
      return { title: "Start by sharpening your opportunity", body: "You have a spark. We'll help you name the real problem and shape it into a clear business you can describe in one line." };
    case "I already have an offer":
      return { title: "Let's pressure-test and package your offer", body: "You're ahead. We'll still lock in your foundation quickly, then focus on validating demand and tightening your offer before launch." };
    case "I am preparing to launch":
      return { title: "Let's get you launch-ready", body: "You're close. We'll confirm each foundation piece is solid, then move fast through validation, offer, and your launch kit." };
    default:
      return { title: "Start at the very beginning, the right way", body: "Perfect place to start. We'll begin with your purpose, then walk step by step to a real offer and your first launch." };
  }
}

const DRAFT_KEY = "lti_onboarding_draft";

export default function Onboarding() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  // step: -1 welcome, 0..N-1 questions, N name, N+1 recommendation
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState("");
  const [generating, setGenerating] = useState(false);

  // restore autosaved progress
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (saved) { setAnswers(saved.answers || {}); setName(saved.name || ""); }
    } catch { /* ignore */ }
  }, []);

  // autosave
  useEffect(() => {
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify({ answers, name })); } catch { /* ignore */ }
  }, [answers, name]);

  const qCount = QUESTIONS.length;
  const nameStep = qCount;
  const recStep = qCount + 1;

  const choose = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    setTimeout(() => setStep((s) => s + 1), 160);
  };
  const skip = () => setStep((s) => s + 1);

  const submit = async () => {
    setGenerating(true);
    try {
      await api.post("/onboarding", { ...answers, business_name: name.trim() || null });
      track("Onboarding Completed", { stage: answers.stage, business_type: answers.business_type });
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      await checkAuth();
      setTimeout(() => navigate("/dashboard", { replace: true }), 1100);
    } catch {
      setGenerating(false);
      toast.error("Something went wrong. Please try again.");
    }
  };

  if (generating) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#FDFCFE]">
        <Loader2 className="animate-spin text-brand" size={30} />
        <p className="font-heading text-lg font-medium">Setting up your workspace…</p>
        <p className="text-sm text-slate-500">Lining up your first step. This takes a moment.</p>
      </div>
    );
  }

  const totalUnits = qCount + 2; // questions + name + recommendation
  const progress = Math.round((Math.max(step, 0) / totalUnits) * 100);

  return (
    <div className="min-h-screen bg-[#FDFCFE]">
      <header className="mx-auto max-w-2xl px-6 py-6"><Logo /></header>
      <div className="mx-auto max-w-2xl px-6 pb-16">
        {step >= 0 && (
          <div className="mb-8 h-1 w-full overflow-hidden rounded-full bg-brand-surface">
            <div className="h-full rounded-full bg-brand transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        )}

        {/* Welcome */}
        {step === -1 && (
          <div className="ltifade" data-testid="onboarding-welcome">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-surface px-3 py-1 text-xs font-medium text-brand-text">
              <Sparkles size={13} /> Welcome
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              Let's turn your idea into a real business
            </h1>
            <p className="mt-3 text-slate-600">
              A few quick questions (under three minutes) so we can point you at the right first step.
              By the end, you'll have:
            </p>
            <div className="mt-5 grid gap-2 sm:grid-cols-2">
              {OUTPUTS.map((o, i) => (
                <div key={o} className="flex items-center gap-2.5 rounded-xl border border-brand-line bg-white p-3 text-sm shadow-soft">
                  <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-brand-surface text-xs font-semibold text-brand-text">{i + 1}</span>
                  {o}
                </div>
              ))}
            </div>
            <button
              data-testid="onboarding-begin-btn"
              onClick={() => setStep(0)}
              className="mt-7 inline-flex items-center gap-2 rounded-lg bg-brand px-6 py-3 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover"
            >
              Let's begin <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* Questions */}
        {step >= 0 && step < nameStep && (() => {
          const q = QUESTIONS[step];
          return (
            <div key={step} className="ltifade">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-brand-muted">
                Question {step + 1} of {qCount}{q.essential ? "" : " · optional"}
              </p>
              <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight">{q.q}</h1>
              <p className="mt-2 text-slate-500">{q.help}</p>
              <div className="mt-7 grid gap-3">
                {q.options.map((opt) => {
                  const active = answers[q.key] === opt;
                  return (
                    <button
                      key={opt}
                      data-testid={`onboarding-option-${q.key}`}
                      onClick={() => choose(q.key, opt)}
                      className={`flex items-center justify-between rounded-xl border p-4 text-left text-sm transition-all duration-200 hover:-translate-y-0.5 ${
                        active ? "border-brand bg-brand-surface" : "border-brand-line bg-white hover:border-brand-muted"
                      }`}
                    >
                      <span className="font-medium">{opt}</span>
                      {active && <Check size={18} className="text-brand" />}
                    </button>
                  );
                })}
              </div>
              <div className="mt-6 flex items-center justify-between">
                {step > 0 ? (
                  <button onClick={() => setStep((s) => s - 1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
                    <ArrowLeft size={15} /> Back
                  </button>
                ) : <span />}
                {!q.essential && (
                  <button data-testid={`onboarding-skip-${q.key}`} onClick={skip} className="text-sm text-slate-400 hover:text-slate-700">
                    Skip for now
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* Name */}
        {step === nameStep && (
          <div className="ltifade">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-brand-muted">Almost there</p>
            <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight">Name your business</h1>
            <p className="mt-2 text-slate-500">A working title is fine. You can rename it any time.</p>
            <input
              data-testid="onboarding-business-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Steady Books, or leave blank for now"
              className="mt-6 w-full rounded-xl border border-brand-line bg-white p-4 text-sm focus:border-brand focus:outline-none"
            />
            <div className="mt-7 flex items-center gap-3">
              <button onClick={() => setStep((s) => s - 1)} className="inline-flex items-center gap-1.5 rounded-lg px-4 py-3 text-sm text-slate-500 hover:text-slate-800">
                <ArrowLeft size={15} /> Back
              </button>
              <button data-testid="onboarding-continue-btn" onClick={() => setStep(recStep)} className="inline-flex items-center gap-2 rounded-lg bg-brand px-6 py-3 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover">
                Continue <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Recommendation + expected outputs */}
        {step === recStep && (() => {
          const rec = recommendation(answers.stage);
          return (
            <div className="ltifade" data-testid="onboarding-recommendation">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                <Target size={13} /> Your recommended starting point
              </span>
              <h1 className="mt-4 font-heading text-3xl font-semibold tracking-tight">{rec.title}</h1>
              <p className="mt-3 text-slate-600">{rec.body}</p>
              <div className="mt-6 rounded-2xl border border-brand-line bg-white p-5 shadow-soft">
                <p className="flex items-center gap-2 text-sm font-medium text-slate-700"><Flag size={15} className="text-brand" /> Your first step</p>
                <p className="mt-1 font-heading text-lg font-semibold">Clarify your purpose</p>
                <p className="mt-1 text-sm text-slate-500">About 15 minutes. You'll finish holding your Purpose Statement.</p>
              </div>
              <div className="mt-6 flex items-center gap-3">
                <button onClick={() => setStep(nameStep)} className="inline-flex items-center gap-1.5 rounded-lg px-4 py-3 text-sm text-slate-500 hover:text-slate-800">
                  <ArrowLeft size={15} /> Back
                </button>
                <button data-testid="onboarding-finish-btn" onClick={submit} className="inline-flex items-center gap-2 rounded-lg bg-brand px-6 py-3 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover">
                  <Rocket size={16} /> Build my workspace
                </button>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
